import { ComponentType, lazy, StrictMode, Suspense } from 'react';
import { CookiesProvider } from 'react-cookie';
import ReactDOM from 'react-dom';
import { HelmetProvider } from 'react-helmet-async';
import { RecoilRoot } from 'recoil';
import { ApplicationContextProvider } from '../../contexts/ApplicationContext';
import { useUserInfo } from '../../hooks/use-user-info';
import { useTranslations } from '../../i18n';

const TranslationsProvider = ({ children }: { children: JSX.Element }) => {
    const userInfo = useUserInfo();
    const tenant = window.OverDrive?.tenant ?? 0;
    const translationsLoaded = useTranslations(true, userInfo.locale, tenant);
    if (!translationsLoaded) {
        return <></>;
    }

    return children;
};

const PureComponentRoot = ({ children }: { children: JSX.Element }) => (
    <StrictMode>
        <TranslationsProvider>
            <Suspense children={children} fallback={<></>} />
        </TranslationsProvider>
    </StrictMode>
);

const PageComponentRoot = ({ children }: { children: JSX.Element }) => (
    <StrictMode>
        <RecoilRoot>
            <HelmetProvider>
                <CookiesProvider>
                    <ApplicationContextProvider>
                        <Suspense fallback={<></>}>{children}</Suspense>
                    </ApplicationContextProvider>
                </CookiesProvider>
            </HelmetProvider>
        </RecoilRoot>
    </StrictMode>
);

/**
 * Renders a pure React component inside the element matching {@link elementId}
 * @returns An object with an update function to refresh the component with new prop values
 */
export const renderComponent =
    (factory: () => Promise<{ default: ComponentType<any> }>) =>
    (elementId: string, props: any) => {
        const Component = lazy(factory);
        ReactDOM.render(
            <PureComponentRoot children={<Component {...props} />} />,
            document.getElementById(elementId)
        );

        return {
            update: (updatedProps: any) => {
                const hydrateProps = { ...props, ...updatedProps };
                ReactDOM.hydrate(
                    <PureComponentRoot
                        children={<Component {...hydrateProps} />}
                    />,
                    document.getElementById(elementId)
                );
            },
        };
    };

/**
 * Renders a fully rigged React page component inside the element matching {@link elementId}
 */
export const renderPage =
    (factory: () => Promise<{ default: ComponentType<any> }>) =>
    (elementId: string, props: any) => {
        const Component = lazy(factory);
        ReactDOM.render(
            <PageComponentRoot children={<Component {...props} />} />,
            document.getElementById(elementId)
        );
    };
